/*
  <gravur-araci> — photo engraving tool on the product page (snippets/gravur.liquid), 2026-09-30.
  The customer uploads an image; GravurMotor (gravur-motor.js) turns it into an engraving map in the chosen style and
  draws it onto the blank coin of every product photo that has a placement. The block's own canvas shows a close-up of
  the coin (drag to move, pinch / wheel / slider to resize), and the gallery photos (and the zoom lightbox, which reads
  the gallery <img>) show the engraved necklace. Before add to cart the original file, the laser file and the preview
  are put into file inputs of the product form; the form stays invalid until they are ready.
*/
(() => {
  if (customElements.get('gravur-araci')) return;

  const ONIZLEME_S = 560;      // engraving map size while editing
  const SURUKLE_S = 300;       // … while dragging (fast)
  const LAZER_S = 1400;        // engraving map for the laser file
  const LAZER_PX = 2000;       // laser file size
  const ADLAR = { cizgi: 'Line Art', foto: 'Photo', damga: 'Stamp' };

  const metalBul = (m) => (/rose/i.test(m || '') ? 'rose' : /silver|white|sterling/i.test(m || '') ? 'silver' : 'gold');
  const resimYukle = (src) => new Promise((ok, hata) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => ok(i); i.onerror = hata; i.src = src; });
  const blob = (cv, tur, kalite) => new Promise((ok) => cv.toBlob(ok, tur, kalite));
  const sonraki = () => new Promise((ok) => requestAnimationFrame(() => setTimeout(ok, 0)));

  class GravurAraci extends HTMLElement {
    connectedCallback() {
      if (this._hazir) return; this._hazir = true;
      const q = (s) => this.querySelector(s);
      this.formId = this.getAttribute('form-id');
      this.malzemeKonum = Number(this.dataset.materialPosition || 0);
      this.metal = metalBul(this.dataset.material);
      this.yerler = JSON.parse(q('[data-yerlesim]').textContent || '[]');
      this.tuval = q('[data-tuval]'); this.sahne = q('[data-sahne]');
      this.dosyaGirdi = q('[data-dosya]'); this.hata = q('[data-hata]'); this.durum = q('[data-durum]');
      this.denetim = q('[data-denetim]');
      this.ayar = { stil: 'cizgi', detay: 0.45, ters: false, olcek: 1, dx: 0, dy: 0, aci: 0 };
      this.tabanlar = new Map();   // media id → Image (blank coin photo)
      this.orijinaller = new Map(); // gallery <img> → {src, srcset, sizes}
      this.surum = 0;

      this.querySelectorAll('[data-sec]').forEach((b) => b.addEventListener('click', () => this.dosyaGirdi.click()));
      this.dosyaGirdi.addEventListener('change', () => { const f = this.dosyaGirdi.files[0]; if (f) this.yukle(f); this.dosyaGirdi.value = ''; });
      q('[data-kaldir]').addEventListener('click', () => this.kaldir());
      this.querySelectorAll('[data-stil]').forEach((r) => r.addEventListener('change', () => { if (r.checked) { this.ayar.stil = r.value; this.degisti(); } }));
      q('[data-detay]').addEventListener('input', (e) => { this.ayar.detay = e.target.value / 100; this.degisti(true); });
      q('[data-detay]').addEventListener('change', () => this.degisti());
      q('[data-olcek]').addEventListener('input', (e) => { this.ayar.olcek = e.target.value / 100; this.degisti(true); });
      q('[data-olcek]').addEventListener('change', () => this.degisti());
      q('[data-dondur]').addEventListener('click', () => { this.ayar.aci = (this.ayar.aci + 90) % 360; this.degisti(); });
      const tersD = q('[data-ters]');
      tersD.addEventListener('click', () => { this.ayar.ters = !this.ayar.ters; tersD.setAttribute('aria-pressed', String(this.ayar.ters)); this.degisti(); });
      q('[data-sifirla]').addEventListener('click', () => { Object.assign(this.ayar, { olcek: 1, dx: 0, dy: 0, aci: 0 }); q('[data-olcek]').value = 100; this.degisti(); });
      this.surukle();
      this.birak();

      // the theme's form calls reportValidity(): point at the tool when the image is missing or not ready yet
      this.denetim.addEventListener('invalid', (e) => {
        e.preventDefault();
        this.hataGoster(this.gorsel ? this.dataset.busyText : this.dataset.emptyText);
        this.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
      this.gecerlilik();

      this._varyant = (e) => {
        const kaynak = e.target instanceof HTMLFormElement ? e.target.getAttribute('id') : null;
        if (kaynak !== this.formId || !e.detail?.variant) return;
        const o = e.detail.variant.options || [];
        const yeni = metalBul(o[this.malzemeKonum - 1]);
        // a new metal changes the photos and the preview file: run the whole pipeline again
        if (yeni !== this.metal) { this.metal = yeni; if (this.gorsel) this.degisti(); else this.ciz(); }
      };
      document.addEventListener('variant:change', this._varyant);
      this.ciz();
    }

    disconnectedCallback() { document.removeEventListener('variant:change', this._varyant); }

    // ——— image in

    async yukle(dosya) {
      this.hataGoster('');
      if (!/^image\//.test(dosya.type) && !/\.(heic|heif)$/i.test(dosya.name)) { this.hataGoster('Please choose an image file (JPG or PNG).'); return; }
      this.durumYaz('Reading your image…');
      let bmp;
      try {
        bmp = await createImageBitmap(dosya, { imageOrientation: 'from-image' });
      } catch {
        try { const u = URL.createObjectURL(dosya); bmp = await resimYukle(u); } catch { bmp = null; }
      }
      if (!bmp) { this.durumYaz(''); this.hataGoster('This image could not be opened. Please try a JPG or PNG.'); return; }
      // work on a copy of at most 1800 px: plenty for the coin, fast on phones
      const k = Math.min(1, 1800 / Math.max(bmp.width, bmp.height));
      const cv = document.createElement('canvas'); cv.width = Math.round(bmp.width * k); cv.height = Math.round(bmp.height * k);
      cv.getContext('2d').drawImage(bmp, 0, 0, cv.width, cv.height);
      this.gorsel = cv; this.dosya = dosya;
      Object.assign(this.ayar, { olcek: 1, dx: 0, dy: 0, aci: 0 });
      // a drawing, handwriting or logo (mostly plain white paper) engraves best as Stamp, a photo as Line Art
      const stil = this.cizimMi(cv) ? 'damga' : 'cizgi';
      this.ayar.stil = stil;
      this.querySelectorAll('[data-stil]').forEach((r) => { r.checked = r.value === stil; });
      this.querySelector('[data-olcek]').value = 100;
      this.classList.add('is-dolu');
      this.querySelector('[data-ayarlar]').hidden = false;
      this.querySelector('[data-dosya-dugmeler]').hidden = false;
      this.querySelector('[data-surukle-not]').hidden = false;
      this.durumYaz('');
      this.degisti();
    }

    cizimMi(cv) {
      const k = document.createElement('canvas'); k.width = k.height = 64;
      const c = k.getContext('2d', { willReadFrequently: true }); c.drawImage(cv, 0, 0, 64, 64);
      const d = c.getImageData(0, 0, 64, 64).data; let beyaz = 0;
      for (let j = 0; j < d.length; j += 4) {
        const mx = Math.max(d[j], d[j + 1], d[j + 2]), mn = Math.min(d[j], d[j + 1], d[j + 2]);
        if (mx > 205 && mx - mn < 40) beyaz++;
      }
      return beyaz / (64 * 64) > 0.45;
    }

    kaldir() {
      this.gorsel = null; this.dosya = null; this.E = null; this.surum++;
      this.classList.remove('is-dolu');
      this.querySelector('[data-ayarlar]').hidden = true;
      this.querySelector('[data-dosya-dugmeler]').hidden = true;
      this.querySelector('[data-surukle-not]').hidden = true;
      this.galeriyiGeriAl();
      this.dosyalariBosalt();
      this.ciz();
    }

    // ——— engraving

    harita(S) {
      const M = window.GravurMotor;
      const { L, D } = M.yerlestir(this.gorsel, S, this.ayar);
      let E = this.ayar.stil === 'foto' ? M.foto(L, S, this.ayar.detay) : this.ayar.stil === 'damga' ? M.damga(D, S, this.ayar.detay) : M.cizgi(L, S, this.ayar.detay);
      if (this.ayar.ters) E = M.ters(E);
      return E;
    }

    // hizli = while a slider or a drag is moving: small map, block canvas only
    degisti(hizli = false) {
      if (!this.gorsel) return;
      const surum = ++this.surum;
      this.dosyalariBosalt();
      cancelAnimationFrame(this._kare);
      this._kare = requestAnimationFrame(async () => {
        if (surum !== this.surum || !window.GravurMotor) return;
        this.E = this.harita(hizli ? SURUKLE_S : ONIZLEME_S);
        await this.ciz();
        if (hizli) return;
        clearTimeout(this._bekle);
        this._bekle = setTimeout(async () => {
          if (surum !== this.surum) return;
          await this.galeriyiGuncelle();
          if (surum === this.surum) await this.dosyalariHazirla(surum);
        }, 250);
      });
    }

    // the placement of the coin photo shown in the block: the front photo of the current metal
    onYer() {
      return this.yerler.find((y) => y.metal === this.metal && y.on) || this.yerler.find((y) => y.metal === this.metal) || this.yerler[0];
    }

    async taban(y) {
      if (!this.tabanlar.has(y.id)) this.tabanlar.set(y.id, resimYukle(y.src));
      return this.tabanlar.get(y.id);
    }

    // pixel placement of the engraving on photo y drawn at width W
    konum(y, W, H) {
      return { cx: y.cx * W, cy: y.cy * H, rx: y.rx * W, ry: y.ry * W, aci: y.aci || 0, donus: y.donus || 0 };
    }

    // the block's close-up: a square around the coin
    async ciz() {
      const y = this.onYer(); if (!y) return;
      let im; try { im = await this.taban(y); } catch { return; }
      const W = im.naturalWidth, H = im.naturalHeight, k = this.konum(y, W, H);
      const yari = Math.max(k.rx, k.ry) * 1.28, x0 = k.cx - yari, y0 = k.cy - yari;
      const T = this.tuval, N = T.width, ctx = T.getContext('2d', { willReadFrequently: true });
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(im, x0, y0, 2 * yari, 2 * yari, 0, 0, N, N);
      this._sahneK = { oran: N / (2 * yari), cx: (k.cx - x0) * (N / (2 * yari)), cy: (k.cy - y0) * (N / (2 * yari)), r: k.rx * (N / (2 * yari)) };
      if (this.gorsel && this.E && window.GravurMotor) {
        const img = ctx.getImageData(0, 0, N, N), o = N / (2 * yari);
        window.GravurMotor.isle(img, { cx: (k.cx - x0) * o, cy: (k.cy - y0) * o, rx: k.rx * o, ry: k.ry * o, aci: k.aci, donus: k.donus }, this.E, y.metal);
        ctx.putImageData(img, 0, 0);
      }
    }

    // ——— gallery

    galeriHucreleri(id) {
      return [...document.querySelectorAll(`.product-gallery__media[data-media-id="${id}"] img, .product-gallery__thumbnail[data-media-id="${id}"] img, [data-media-id="${id}"] .product-gallery__thumbnail img`)];
    }

    async galeriyiGuncelle() {
      if (!this.gorsel || !this.E) return;
      const surum = this.surum;
      for (const y of this.yerler.filter((x) => x.metal === this.metal)) {
        let im; try { im = await this.taban(y); } catch { continue; }
        await sonraki();
        if (surum !== this.surum) return;
        const W = im.naturalWidth, H = im.naturalHeight, k = this.konum(y, W, H);
        const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
        const ctx = cv.getContext('2d', { willReadFrequently: true }); ctx.drawImage(im, 0, 0);
        const yari = Math.ceil(Math.max(k.rx, k.ry) * 1.1), x0 = Math.max(0, Math.floor(k.cx - yari)), y0 = Math.max(0, Math.floor(k.cy - yari));
        const w = Math.min(W - x0, 2 * yari), h = Math.min(H - y0, 2 * yari);
        const parca = ctx.getImageData(x0, y0, w, h);
        window.GravurMotor.isle(parca, { ...k, cx: k.cx - x0, cy: k.cy - y0 }, this.E, y.metal);
        ctx.putImageData(parca, x0, y0);
        if (y === this.onYer()) this._onTuval = cv;      // the order preview is made from this one
        const b = await blob(cv, 'image/jpeg', 0.9);
        if (surum !== this.surum || !b) return;
        const url = URL.createObjectURL(b);
        for (const img of this.galeriHucreleri(y.id)) {
          if (!this.orijinaller.has(img)) this.orijinaller.set(img, { src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), sizes: img.getAttribute('sizes') });
          const eski = img.dataset.gravurUrl;
          img.removeAttribute('srcset'); img.src = url; img.dataset.gravurUrl = url;
          if (eski && eski !== url) setTimeout(() => URL.revokeObjectURL(eski), 4000);
        }
      }
    }

    galeriyiGeriAl() {
      for (const [img, o] of this.orijinaller) {
        if (o.srcset) img.setAttribute('srcset', o.srcset);
        if (o.src) img.setAttribute('src', o.src);
        if (img.dataset.gravurUrl) { URL.revokeObjectURL(img.dataset.gravurUrl); delete img.dataset.gravurUrl; }
      }
      this.orijinaller.clear();
    }

    // ——— order files

    dosyalariBosalt() {
      this.hazir = false;
      for (const s of ['[data-orijinal]', '[data-lazer]', '[data-onizleme-dosya]']) { const g = this.querySelector(s); try { g.files = new DataTransfer().files; } catch { g.value = ''; } }
      this.querySelector('[data-stil-ozet]').disabled = true;
      this.querySelector('[data-ayar-ozet]').disabled = true;
      this.gecerlilik();
    }

    async dosyalariHazirla(surum) {
      if (!this.gorsel) return;
      this.durumYaz(this.dataset.busyText);
      await sonraki();
      const M = window.GravurMotor;
      const EL = this.harita(LAZER_S);
      if (surum !== this.surum) return;
      const lazer = await blob(M.lazer(EL, LAZER_PX), 'image/png');
      // preview for the order and the cart: the engraved front photo, 1000 px wide (the block's close-up if missing)
      let kaynak = this.tuval;
      if (this._onTuval) {
        const o = this._onTuval, k = 1000 / o.width;
        kaynak = document.createElement('canvas'); kaynak.width = 1000; kaynak.height = Math.round(o.height * k);
        const kc = kaynak.getContext('2d'); kc.imageSmoothingQuality = 'high'; kc.drawImage(o, 0, 0, kaynak.width, kaynak.height);
      }
      const onizleme = await blob(kaynak, 'image/jpeg', 0.88);
      if (surum !== this.surum || !lazer || !onizleme) return;
      const koy = (s, f) => { const dt = new DataTransfer(); dt.items.add(f); this.querySelector(s).files = dt.files; };
      const ad = (this.dosya.name || 'image').replace(/\.[^.]+$/, '').replace(/[^\w-]+/g, '-').slice(0, 40) || 'image';
      koy('[data-orijinal]', this.dosya);
      koy('[data-lazer]', new File([lazer], `${ad}-engraving.png`, { type: 'image/png' }));
      koy('[data-onizleme-dosya]', new File([onizleme], `${ad}-preview.jpg`, { type: 'image/jpeg' }));
      const stil = this.querySelector('[data-stil-ozet]'), ayarlar = this.querySelector('[data-ayar-ozet]');
      stil.value = (ADLAR[this.ayar.stil] || this.ayar.stil) + (this.ayar.ters ? ' · Inverted' : ''); stil.disabled = false;
      const r = (v) => Math.round(v * 1000) / 1000;
      ayarlar.value = JSON.stringify({ stil: this.ayar.stil, detay: r(this.ayar.detay), ters: this.ayar.ters, olcek: r(this.ayar.olcek), dx: r(this.ayar.dx), dy: r(this.ayar.dy), aci: this.ayar.aci, metal: this.metal });
      ayarlar.disabled = false;
      this.hazir = true;
      this.durumYaz('');
      this.hataGoster('');
      this.gecerlilik();
    }

    gecerlilik() {
      this.denetim.setCustomValidity(!this.gorsel ? this.dataset.emptyText : !this.hazir ? this.dataset.busyText : '');
    }

    // ——— moving and resizing on the block's canvas

    surukle() {
      const T = this.tuval; const aktif = new Map(); let bas = null;
      const nokta = (e) => { const r = T.getBoundingClientRect(); return { x: ((e.clientX - r.left) / r.width) * T.width, y: ((e.clientY - r.top) / r.height) * T.height }; };
      T.addEventListener('pointerdown', (e) => {
        if (!this.gorsel) { this.dosyaGirdi.click(); return; }
        T.setPointerCapture(e.pointerId); aktif.set(e.pointerId, nokta(e));
        bas = { ayar: { ...this.ayar }, noktalar: new Map(aktif) };
        e.preventDefault();
      });
      T.addEventListener('pointermove', (e) => {
        if (!aktif.has(e.pointerId) || !bas) return;
        aktif.set(e.pointerId, nokta(e));
        const cap = 2 * (this._sahneK?.r || T.width / 3);
        if (aktif.size === 1) {
          const [id, p] = [...aktif][0], p0 = bas.noktalar.get(id); if (!p0) return;
          this.ayar.dx = Math.max(-0.8, Math.min(0.8, bas.ayar.dx + (p.x - p0.x) / cap));
          this.ayar.dy = Math.max(-0.8, Math.min(0.8, bas.ayar.dy + (p.y - p0.y) / cap));
        } else if (aktif.size === 2) {
          const [a, b] = [...aktif.values()], [a0, b0] = [...bas.noktalar.values()];
          if (!a0 || !b0) return;
          const d = Math.hypot(a.x - b.x, a.y - b.y), d0 = Math.hypot(a0.x - b0.x, a0.y - b0.y) || 1;
          this.ayar.olcek = Math.max(1, Math.min(3, bas.ayar.olcek * (d / d0)));
          this.querySelector('[data-olcek]').value = Math.round(this.ayar.olcek * 100);
        }
        this.degisti(true);
      });
      const bitir = (e) => { if (!aktif.has(e.pointerId)) return; aktif.delete(e.pointerId); bas = aktif.size ? { ayar: { ...this.ayar }, noktalar: new Map(aktif) } : null; if (!aktif.size) this.degisti(); };
      T.addEventListener('pointerup', bitir); T.addEventListener('pointercancel', bitir);
      T.addEventListener('wheel', (e) => {
        if (!this.gorsel) return; e.preventDefault();
        this.ayar.olcek = Math.max(1, Math.min(3, this.ayar.olcek * (e.deltaY < 0 ? 1.06 : 1 / 1.06)));
        this.querySelector('[data-olcek]').value = Math.round(this.ayar.olcek * 100);
        this.degisti(true); clearTimeout(this._teker); this._teker = setTimeout(() => this.degisti(), 300);
      }, { passive: false });
      T.addEventListener('keydown', (e) => {
        if (!this.gorsel) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.dosyaGirdi.click(); } return; }
        const adim = e.shiftKey ? 0.05 : 0.01, m = { ArrowLeft: [-adim, 0], ArrowRight: [adim, 0], ArrowUp: [0, -adim], ArrowDown: [0, adim] }[e.key];
        if (!m) return; e.preventDefault();
        this.ayar.dx = Math.max(-0.8, Math.min(0.8, this.ayar.dx + m[0])); this.ayar.dy = Math.max(-0.8, Math.min(0.8, this.ayar.dy + m[1]));
        this.degisti();
      });
    }

    // dropping a file onto the tool
    birak() {
      this.addEventListener('dragover', (e) => { if ([...(e.dataTransfer?.items || [])].some((i) => i.kind === 'file')) { e.preventDefault(); this.classList.add('is-uzerinde'); } });
      this.addEventListener('dragleave', () => this.classList.remove('is-uzerinde'));
      this.addEventListener('drop', (e) => { const f = e.dataTransfer?.files?.[0]; this.classList.remove('is-uzerinde'); if (f) { e.preventDefault(); this.yukle(f); } });
    }

    durumYaz(t) { this.durum.textContent = t || ''; }
    hataGoster(t) { this.hata.textContent = t || ''; this.hata.hidden = !t; }
  }

  customElements.define('gravur-araci', GravurAraci);
})();
